import type { ReactNode } from 'react';
import React from 'react';

import type {
  ComponentRenderProps,
  FlexProps,
} from '../../../types/dynamic_views';

interface FlexComponentProps extends ComponentRenderProps {
  children: ReactNode;
}

/**
 * Layout container — never interactive. See SectionComponent for rationale.
 */
export const FlexComponent: React.FC<FlexComponentProps> = ({
  component,
  className = '',
  children,
}) => {
  const props = component.props as FlexProps;
  const {
    direction = 'row',
    justify = 'start',
    align = 'start',
    gap = 'md',
    wrap = false,
  } = props;

  // Build CSS classes
  const directionClasses = {
    row: 'flex-row',
    column: 'flex-col',
  };

  const justifyClasses = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
    around: 'justify-around',
  };

  const alignClasses = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch',
  };

  const gapClasses = {
    none: 'gap-0',
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6',
  };

  const allClasses = [
    'flex',
    directionClasses[direction] || directionClasses.row,
    justifyClasses[justify] || justifyClasses.start,
    alignClasses[align] || alignClasses.start,
    gapClasses[gap] || gapClasses.md,
    wrap ? 'flex-wrap' : 'flex-nowrap',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={allClasses}
      data-component-id={component.id}
      data-direction={direction}
    >
      {children}
    </div>
  );
};

export default FlexComponent;
